import React, { ReactNode, useMemo } from 'react';

import {
  FieldInputProps,
  FieldProps,
  FieldRenderProps,
  useField,
} from 'react-final-form';

import { AddressInput as Input } from '@vk-hr-tek/ui/input';

import { composeValidators } from '../validation';

/**
 * Функция-обработчик изменения значения ввода.
 * @param {FieldInputProps} input - Объект с пропсами ввода из react-final-form.
 * @param {Function} [onChange] - Дополнительная функция-обработчик изменения значения.
 * @returns {Function} Функция, которая вызывает onChange из react-final-form и дополнительную onChange, если она передана.
 */
const handleChange =
  (
    input: FieldInputProps<
      string | string[] | number | number[] | undefined,
      HTMLElement
    >,
    onChange?: (value: string | undefined) => void,
  ) =>
  (value: string | undefined) => {
    input.onChange(value);

    if (onChange) {
      onChange(value);
    }
  };

const requestErr =
  'Сервис подсказки адресов временно недоступен. Повторите попытку позднее';
const completenessErr =
  'Нужно выбрать адрес из списка с содержанием: индекс, регион, населенный пункт, улица, дом/квартира';

export interface Filter {
  limit: number;
  offset: number;
  query: string;
}

/**
 * Интерфейс свойств компонента
 *
 * @interface AddressInputProps
 * @property {string} name - Атрибут name для поля ввода.
 * @property {string} label - Текст метки для поля ввода.
 * @property {string | null} value - Текущее значение поля ввода.
 * @property {(value: string | undefined) => void} onChange - Функция обратного вызова для обработки изменений значения ввода.
 * @property {() => void} [onBlur] - Функция обратного вызова для обработки события потери фокуса.
 * @property {string} [placeholder] - Текст плейсхолдера для поля ввода.
 * @property {boolean} [disabled=false] - Если true, поле ввода будет отключено.
 * @property {boolean} [required=false] - Если true, поле ввода будет отмечено как обязательное.
 * @property {boolean} [clearable=false] - Если true, поле ввода будет иметь кнопку очистки.
 * @property {boolean} [loading=false] - Если true, поле ввода будет показывать индикатор загрузки.
 * @property {ReactNode | null} [tooltip=null] - Содержимое всплывающей подсказки, отображаемой с меткой.
 * @property {boolean} [error=false] - Если true, поле ввода будет отмечено как содержащее ошибку.
 * @property {string | ReactNode} [helperText] - Вспомогательный текст, отображаемый с полем ввода.
 * @property {(option: string) => React.ReactNode} [renderOption] - Пользовательская функция отображения вариантов.
 * @property {number} [limit=50] - Максимальное количество элементов для запроса.
 * @property {number} [minInputValueLength=1] - Минимальная длина ввода, необходимая для вызова подсказок.
 * @property {string} [mask] - Маска, применяемая к полю ввода.
 * @property {number} [addressCompleteness=9] - Определяет полноту заполненности адреса
 *   0 — страна
 *   1 — регион
 *   3 — район
 *   4 — город
 *   5 — район города
 *   6 — населенный пункт
 *   7 — улица
 *   8 — дом
 *   9 — квартира или комната
 *  65 — планировочная структура
 *  75 — земельный участок
 *  -1 — иностранный или пустой
 * @property {boolean} [props.validationWithoutTouch=false] - Показывать ошибку без взаимодействия с полем.
 * @property {boolean} [props.validationWhenDisabled=false] - Показывать ошибку если поле задизейблено.
 * */

interface AddressInputProps {
  name: string;
  loadItems: (
    filter: Filter,
  ) => Promise<{ value: string; addressCompleteness?: number }[]>;
  label?: string;
  onChange?: (value: string | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  clearable?: boolean;
  loading?: boolean;
  tooltip?: ReactNode | null;
  helperText?: string;
  noOptionsText?: string;
  renderOption?: (option: string) => ReactNode;
  limit?: number;
  minInputValueLength?: number;
  addressCompleteness?: number;
  mask?: string;
  validationWithoutTouch?: boolean;
  validationWhenDisabled?: boolean;
}

/**
 * Компонент ввода с подсказками.
 * @param {AddressInputProps & FieldProps} props - Пропсы компонента, объединяющие пропсы AddressInput и FieldProps из react-final-form.
 * @returns {JSX.Element} Компонент Field, который рендерит кастомный компонент ввода Input.
 */
export const AddressInput = (
  props: AddressInputProps &
    FieldProps<string | undefined, FieldRenderProps<string | undefined>>,
) => {
  const {
    name,
    label,
    onChange,
    placeholder,
    disabled = false,
    required = false,
    loading = false,
    tooltip,
    helperText,
    noOptionsText,
    renderOption,
    limit = 50,
    minInputValueLength = 1,
    addressCompleteness = 8,
    mask,
    validate,
    validationWithoutTouch = false,
    validationWhenDisabled = false,
    ...rest
  } = props;

  const cacheItems = useMemo(
    () => new Map<string, { value: string; addressCompleteness?: number }[]>(),
    [],
  );

  const internalValidErr = async (value?: string) => {
    try {
      let list: { value: string; addressCompleteness?: number }[] | undefined =
        [];

      if (value && cacheItems.has(value)) {
        list = cacheItems.get(value);
      } else {
        list = await props.loadItems({
          limit,
          offset: 0,
          query: value || '',
        });

        if (!value) {
          return undefined;
        }

        cacheItems.set(value, list);
      }

      const item = list?.find((el) => el.value === value);
      const completeness = item?.addressCompleteness || 0;

      if (addressCompleteness > completeness) {
        return completenessErr;
      }
    } catch {
      return requestErr;
    }

    return undefined;
  };

  const { input, meta } = useField(name, {
    validate: composeValidators(
      validate ? [validate, internalValidErr] : [internalValidErr],
    ),
    ...rest,
  });

  const touched = meta.touched || meta.error === requestErr;

  /**
   * Флаг для отображения ошибки в зависимости от состояния disabled
   * @type {boolean}
   */
  const errorDisabled = disabled || !validationWhenDisabled;

  /**
   * Флаг для отображения ошибки
   * @type {boolean}
   */
  const renderError =
    !errorDisabled && validationWithoutTouch
      ? !!input.value && !!meta.error
      : touched && !!meta.error;

  return (
    <Input
      {...input}
      label={label}
      onChange={handleChange(input, onChange)}
      placeholder={placeholder}
      disabled={disabled}
      required={required}
      loading={loading}
      tooltip={tooltip}
      error={renderError}
      helperText={(renderError && meta.error) || helperText}
      noOptionsText={noOptionsText}
      renderOption={renderOption}
      loadItems={props.loadItems}
      limit={limit}
      mask={mask}
      addressCompleteness={addressCompleteness}
      minInputValueLength={minInputValueLength}
    />
  );
};
